import { AxiosResponse } from 'axios'
import { useApiClient } from './useApiClient'
import {
    Guid,
    ApiResult,
    EmptyApiResult,
    UserViewModel,
    LicensedAgencyViewModel,
    CustomerUserViewModel,
    CustomerAdminViewModel,
    CustomerDeviceViewModel,
    CustomerCertificateViewModel,
    CloudMedia,
    Case,
    DocumentType,
    DeviceMediaUploadStatus,
    LicensedAgencyConfigurationV1,
    PriorityV1,
    StatusV1,
    DocumentTypeV1,
    MapLayer,
    AddMapLayerResult,
    AddMapLayerRequest,
    EditMapLayerResult,
    GetMapLayersResult,
    RecoverableMedia,
} from '../api'
import { CustomerUser, CustomerAdmin, CustomerDevice, EditMapLayerRequest } from 'contexts/base'
import qs from 'qs'
import { encode as encodeBase64 } from 'base64-ts'

export const useAgencyApi = () => {
  const apiClient = useApiClient()

  const getAgencies = () => {
    return apiClient.request<
      void,
      AxiosResponse<ApiResult<LicensedAgencyViewModel[]>>
    >({
      url: 'api/assisting/agencies',
      method: 'get',
      withCredentials: true,
    })
  }

  const getAuthorizedAgencies = () => {
    return apiClient.request<
      void,
      AxiosResponse<ApiResult<LicensedAgencyViewModel[]>>
    >({
      url: 'api/assisting/agencies/authorized',
      method: 'get',
      withCredentials: true,
    })
  }

  const assistAgency = (licensedAgencyId: Guid) => {
    return apiClient.request<void, AxiosResponse<EmptyApiResult>>({
      url: `/api/assisting/agency/${licensedAgencyId}/assist`,
      method: 'post',
      withCredentials: true,
    })
  }

  const unassistAgency = () => {
    return apiClient.request<void, AxiosResponse<EmptyApiResult>>({
      url: '/api/assisting/agency/unassist',
      method: 'post',
      withCredentials: true,
    })
  }

  const getAgency = () => {
    return apiClient.request<
      void,
      AxiosResponse<ApiResult<LicensedAgencyViewModel>>
    >({
      url: `api/assisting/agency`,
      method: 'get',
      withCredentials: true,
    })
  }

  const getAllUsers = () => {
    return apiClient.request<void, AxiosResponse<ApiResult<UserViewModel[]>>>({
      url: `api/assisting/users`,
      method: 'get',
      withCredentials: true,
    })
  }

  const getLicensedUsers = () => {
    return apiClient.request<
      void,
      AxiosResponse<ApiResult<CustomerUserViewModel[]>>
    >({
      url: `api/assisting/licensedUsers`,
      method: 'get',
      withCredentials: true,
    })
  }

  const addLicensedUser = (user: Partial<CustomerUser>) => {
    const fieldsToExclude = ['permissions', 'capabilities']
    const formData = new FormData()
    Object.keys(user)
      .filter((key) => !fieldsToExclude.includes(key))
      .forEach((key: string) => {
        formData.append(key, (user as any)[key])
      })

    return apiClient.request<
      void,
      AxiosResponse<ApiResult<CustomerUserViewModel>>
    >({
      url: `api/assisting/licensedUsers`,
      method: 'post',
      data: formData,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      withCredentials: true,
    })
  }

  const editLicensedUser = (user: Partial<CustomerUser>) => {
    return apiClient.request<
      void,
      AxiosResponse<ApiResult<CustomerUserViewModel>>
    >({
      url: `api/assisting/licensedUsers`,
      method: 'put',
      data: user,
      withCredentials: true,
    })
  }

  const deleteLicensedUser = (licensedUserId: Guid) => {
    return apiClient.request<void, AxiosResponse<EmptyApiResult>>({
      url: `api/assisting/licensedUsers/${licensedUserId}`,
      method: 'delete',
      withCredentials: true,
    })
  }

  const getAdminUsers = () => {
    return apiClient.request<
      void,
      AxiosResponse<ApiResult<CustomerAdminViewModel[]>>
    >({
      url: `api/assisting/adminUsers`,
      method: 'get',
      withCredentials: true,
    })
  }

  const addAdminUser = (user: CustomerAdmin) => {
    return apiClient.request<
      void,
      AxiosResponse<ApiResult<CustomerAdminViewModel>>
    >({
      url: `api/assisting/adminUsers`,
      method: 'post',
      data: user,
      withCredentials: true,
    })
  }

  const editAdminUser = (user: CustomerAdmin) => {
    return apiClient.request<
      void,
      AxiosResponse<ApiResult<CustomerAdminViewModel>>
    >({
      url: `api/assisting/adminUsers`,
      method: 'put',
      data: user,
      withCredentials: true,
    })
  }

  const deleteAdminUser = (customerUserId: Guid) => {
    return apiClient.request<void, AxiosResponse<EmptyApiResult>>({
      url: `api/assisting/adminUsers/${customerUserId}`,
      method: 'delete',
      withCredentials: true,
    })
  }

  const getDevices = () => {
    return apiClient.request<
      void,
      AxiosResponse<ApiResult<CustomerDeviceViewModel[]>>
    >({
      url: `api/assisting/devices`,
      method: 'get',
      withCredentials: true,
    })
  }

  const addDevice = (device: CustomerDevice) => {
    const formData = new FormData()
    Object.keys(device).forEach((key: string) => {
      formData.append(key, (device as any)[key])
    })

    return apiClient.request<
      void,
      AxiosResponse<ApiResult<CustomerDeviceViewModel>>
    >({
      url: `api/assisting/devices`,
      method: 'post',
      data: formData,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      withCredentials: true,
    })
  }

  const editDevice = (device: CustomerDevice) => {
    const formData = new FormData()
    Object.keys(device).forEach((key: string) => {
      formData.append(key, (device as any)[key])
    })

    return apiClient.request<
      void,
      AxiosResponse<ApiResult<CustomerDeviceViewModel>>
    >({
      url: `api/assisting/devices`,
      method: 'put',
      data: device,
      withCredentials: true,
    })
  }

  const deleteDevice = (deviceId: string) => {
    return apiClient.request<void, AxiosResponse<EmptyApiResult>>({
      url: `api/assisting/devices/${deviceId}`,
      method: 'delete',
      withCredentials: true,
    })
  }

  const replaceDevice = (device: CustomerDevice) => {
    return apiClient.request<
      void,
      AxiosResponse<ApiResult<CustomerDeviceViewModel>>
    >({
      url: `api/assisting/devices`,
      method: 'patch',
      data: device,
      withCredentials: true,
    })
  }

  const generateDevicePin = (deviceId: Guid) => {
    return apiClient.request<
      void,
      AxiosResponse<ApiResult<CustomerDeviceViewModel>>
    >({
      url: `api/assisting/devices/${deviceId}/generatePin`,
      method: 'post',
      withCredentials: true,
    })
  }

  const sendDevicePin = (deviceId: Guid) => {
    return apiClient.request<
      void,
      AxiosResponse<ApiResult<Boolean>>
    >({
      url: `api/assisting/devices/${deviceId}/sendPin`,
      method: 'post',
      withCredentials: true,
    })
  }

  const getDeviceCertificateHistory = (deviceId: Guid) => {
    const response = apiClient.request<
      void,
      AxiosResponse<ApiResult<CustomerCertificateViewModel[]>>
    >({
      url: `api/assisting/devices/${deviceId}/certificatehistory`,
      method: 'get',
      withCredentials: true,
    })
    return response
  }

  const searchCases = (
    deviceId: Guid,
    caseNumber: string,
    startDate: string,
    endDate: string,
  ) => {
    return apiClient.request<void, AxiosResponse<ApiResult<Case[]>>>({
      url: `api/assisting/devices/${deviceId}/cases/search`,
      method: 'get',
      params: {
        caseNumber,
        startDate,
        endDate,
      },
      withCredentials: true,
    })
  }

  const getRecoverableMedia = (deviceId: Guid) => {
    const response = apiClient.request<
      void,
      AxiosResponse<ApiResult<RecoverableMedia[]>>
    >({
      url: `api/assisting/devices/${deviceId}/media/recoverable`,
      method: 'get',
      withCredentials: true,
    })
    return response
  }

  const recoverMedia = (
    deviceId: Guid,
    documentType: DocumentType,
    documentId: string,
  ) => {
    return apiClient.request<void, AxiosResponse<EmptyApiResult>>({
      url: `api/assisting/devices/${deviceId}/media/recover`,
      method: 'post',
      params: {
        documentType,
        documentId,
      },
      withCredentials: true,
    })
  }

  const getMediaRecoveryStatus = (deviceId: Guid) => {
    return apiClient.request<
      void,
      AxiosResponse<ApiResult<MediaRecoveryStatus>>
    >({
      url: `api/assisting/devices/${deviceId}/media/recovery/status`,
      method: 'get',
      withCredentials: true,
    })
  }

  const getLastMediaRecoveryDateTime = (deviceId: Guid) => {
    return apiClient.request<void, AxiosResponse<ApiResult<Date | null>>>({
      url: `api/assisting/devices/${deviceId}/media/recovery/lastcompleted`,
      method: 'get',
      withCredentials: true,
    })
  }

  const getLicensedAgencyConfiguration = (licensedAgencyId: Guid) => {
    return apiClient.request<
      void,
      AxiosResponse<ApiResult<LicensedAgencyConfigurationV1>>
    >({
      url: `api/assisting/agency/${licensedAgencyId}/configuration`,
      method: 'get',
      withCredentials: true,
    })
  }

  const editLicensedAgencyConfiguration = (
    licensedAgencyId: Guid,
    configuration: LicensedAgencyConfigurationV1,
  ) => {
    return apiClient.request<void, AxiosResponse<EmptyApiResult>>({
      url: `api/assisting/agency/${licensedAgencyId}/configuration`,
      method: 'put',
      data: configuration,
      withCredentials: true,
    })
  }

  const getPriorities = (
    licensedAgencyId: Guid,
    activeOnly: boolean,
    priorityIds: string[],
  ) => {
    return apiClient.request<void, AxiosResponse<ApiResult<PriorityV1[]>>>({
      url: `api/assisting/agency/${licensedAgencyId}/priorities`,
      method: 'get',
      params: {
        activeOnly,
        priorityIds,
      },
      paramsSerializer: (params) => {
        return qs.stringify(params)
      },
      withCredentials: true,
    })
  }

  const getStatuses = (
    licensedAgencyId: Guid,
    type: DocumentTypeV1,
    activeOnly: boolean,
    excludeClosedStatuses: boolean,
    statusIds: string[],
  ) => {
    return apiClient.request<void, AxiosResponse<ApiResult<StatusV1[]>>>({
      url: `api/assisting/agency/${licensedAgencyId}/statuses`,
      method: 'get',
      params: {
        type,
        activeOnly,
        excludeClosedStatuses,
        statusIds,
      },
      paramsSerializer: (params) => {
        return qs.stringify(params)
      },
      withCredentials: true,
    })
  }

  const getCustomMapLayers = () => {
    return apiClient.request<
      void,
      AxiosResponse<ApiResult<GetMapLayersResult>>
    >({
      url: `api/assisting/map/layers`,
      method: 'get',
      withCredentials: true,
    })
  }

  const addCustomMapLayer = async (mapLayer: MapLayer, file: File, onProgress?: (progress: ProgressEvent) => void) => {

    const request: AddMapLayerRequest = {
      name: mapLayer.name,
      displayName: mapLayer.displayName,
      notes: mapLayer.notes,
      sharedAgencies: mapLayer.sharedAgencies,
      file: {
        name: file.name,
        content: encodeBase64(new Uint8Array(await file.arrayBuffer()))
      }
    }

    return apiClient.request<
      void,
      AxiosResponse<ApiResult<AddMapLayerResult>>
    >({
      url: `api/assisting/map/layers`,
      method: 'put',
      data: request,
      withCredentials: true,
      onUploadProgress: onProgress
    })
  }

  const editCustomMapLayer = async (mapLayer: MapLayer, file?: File, onProgress?: (progress: ProgressEvent) => void) => {

    const request: EditMapLayerRequest = {
      name: mapLayer.name,
      displayName: mapLayer.displayName,
      notes: mapLayer.notes,
      sharedAgencies: mapLayer.sharedAgencies,
      file: !!file
        ? {
          name: file.name,
          content: encodeBase64(new Uint8Array(await file.arrayBuffer()))
        } : undefined
    }

    return apiClient.request<
      void,
      AxiosResponse<ApiResult<EditMapLayerResult>>
    >({
      url: `api/assisting/map/layers/${mapLayer.id}`,
      method: 'put',
      data: request,
      withCredentials: true,
      onUploadProgress: onProgress
    })
  }

  const deleteCustomMapLayer = (id: string) => {
    return apiClient.request<void, AxiosResponse<EmptyApiResult>>({
      url: `api/assisting/map/layers/${id}`,
      method: 'delete',
      withCredentials: true,
    })
  }

  return {
    getAgencies,
    getAuthorizedAgencies,
    getAgency,
    assistAgency,
    unassistAgency,
    getAllUsers,
    getAdminUsers,
    addAdminUser,
    editAdminUser,
    deleteAdminUser,
    getLicensedUsers,
    addLicensedUser,
    editLicensedUser,
    deleteLicensedUser,
    getDevices,
    addDevice,
    editDevice,
    deleteDevice,
    replaceDevice,
    generateDevicePin,
    sendDevicePin,
    getDeviceCertificateHistory,
    searchCases,
    getRecoverableMedia,
    recoverMedia,
    getMediaRecoveryStatus,
    getLastMediaRecoveryDateTime,
    getLicensedAgencyConfiguration,
    editLicensedAgencyConfiguration,
    getPriorities,
    getStatuses,
    getCustomMapLayers,
    addCustomMapLayer,
    editCustomMapLayer,
    deleteCustomMapLayer,
  }
}
